import React from 'react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'

interface AddressDisplayProps {
  address: string
  name?: string | null      // Pre-resolved X1NS name from parent
  truncateLength?: number   // chars from start (default 8)
  truncateEnd?: number      // chars from end (default 6)
  linkToAccount?: boolean
  className?: string
  mono?: boolean
}

/**
 * Lightweight inline address display for use in tables/lists.
 * Name resolution is done by the parent via useAddressNames hook —
 * this component does NO internal API calls.
 */
export const AddressDisplay: React.FC<AddressDisplayProps> = ({
  address,
  name,
  truncateLength = 8,
  truncateEnd = 6,
  linkToAccount = true,
  className = '',
  mono = true,
}) => {
  if (!address) return <span className="text-gray-400">-</span>

  const truncated = address.length > truncateLength + truncateEnd + 3
    ? `${address.slice(0, truncateLength)}...${address.slice(-truncateEnd)}`
    : address

  const content = name ? (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-primary font-medium">{name}</span>
      <span className={clsx('text-gray-400 text-xs', mono && 'font-mono')}>
        ({address.slice(0, 4)}...{address.slice(-4)})
      </span>
    </span>
  ) : (
    <span className={clsx(mono && 'font-mono')}>{truncated}</span>
  )

  if (linkToAccount) {
    return (
      <Link
        to={`/account/${address}`}
        className={clsx('hover:text-primary transition-colors', className)}
        title={address}
      >
        {content}
      </Link>
    )
  }

  return <span className={className} title={address}>{content}</span>
}

export default AddressDisplay
